/**
 * AI Service Error Handler
 * 
 * Provides comprehensive error handling, logging, and user-friendly error messages
 * for the AI document processing system. Includes fallback strategies and
 * error categorization for better debugging and user experience.
 */

import { AI_ENVIRONMENT } from '../config/environment';

/**
 * Error categories for different types of AI processing failures
 */
export const ERROR_CATEGORIES = {
  API_KEY: 'api_key',
  RATE_LIMIT: 'rate_limit',
  TIMEOUT: 'timeout',
  NETWORK: 'network',
  FILE_SIZE: 'file_size',
  FILE_TYPE: 'file_type',
  AI_PROCESSING: 'ai_processing',
  QUOTA_EXCEEDED: 'quota_exceeded',
  VALIDATION: 'validation',
  UNKNOWN: 'unknown'
};

/**
 * User-friendly error messages for different error categories
 */
const ERROR_MESSAGES = {
  [ERROR_CATEGORIES.API_KEY]: 'AI service is not configured properly. Please contact support.',
  [ERROR_CATEGORIES.RATE_LIMIT]: 'Too many requests. Please wait a moment and try again.',
  [ERROR_CATEGORIES.TIMEOUT]: 'AI processing is taking longer than expected. The document has been uploaded but may not be categorized.',
  [ERROR_CATEGORIES.NETWORK]: 'Network connection issue. Please check your internet connection and try again.',
  [ERROR_CATEGORIES.FILE_SIZE]: 'File is too large for AI processing. Maximum size for AI analysis is 20MB.',
  [ERROR_CATEGORIES.FILE_TYPE]: 'File type not supported for AI processing. Supported types: PDF, PNG, JPG, JPEG.',
  [ERROR_CATEGORIES.AI_PROCESSING]: 'AI processing failed. The document has been uploaded but may not be automatically categorized.',
  [ERROR_CATEGORIES.QUOTA_EXCEEDED]: 'AI processing quota exceeded. The document has been uploaded but cannot be processed with AI today.',
  [ERROR_CATEGORIES.VALIDATION]: 'Invalid request. Please check your input and try again.',
  [ERROR_CATEGORIES.UNKNOWN]: 'An unexpected error occurred. Please try again or contact support.'
};

/**
 * Determines if an error is retryable
 */
const RETRYABLE_ERRORS = [
  ERROR_CATEGORIES.TIMEOUT,
  ERROR_CATEGORIES.NETWORK,
  ERROR_CATEGORIES.RATE_LIMIT
];

/**
 * AI Error Handler Class
 */
export class AIErrorHandler {
  constructor() {
    this.errorLog = [];
    this.maxLogEntries = 100;
  }

  /**
   * Categorizes an error based on its characteristics
   * @param {Error} error - The error to categorize
   * @returns {string} Error category
   */
  categorizeError(error) {
    const message = error.message.toLowerCase();
    
    // API key errors
    if (message.includes('api key') || message.includes('unauthorized') || message.includes('authentication')) {
      return ERROR_CATEGORIES.API_KEY;
    }
    
    // Rate limiting errors
    if (message.includes('rate limit') || message.includes('too many requests') || error.status === 429) {
      return ERROR_CATEGORIES.RATE_LIMIT;
    }
    
    // Timeout errors
    if (message.includes('timeout') || message.includes('aborted') || error.name === 'AbortError') {
      return ERROR_CATEGORIES.TIMEOUT;
    }
    
    // Network errors
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return ERROR_CATEGORIES.NETWORK;
    }
    
    // File size errors
    if (message.includes('file too large') || message.includes('maximum size')) {
      return ERROR_CATEGORIES.FILE_SIZE;
    }
    
    // File type errors
    if (message.includes('unsupported file type') || message.includes('file type')) {
      return ERROR_CATEGORIES.FILE_TYPE;
    }
    
    // Quota errors
    if (message.includes('quota') || message.includes('limit exceeded') || error.status === 402) {
      return ERROR_CATEGORIES.QUOTA_EXCEEDED;
    }
    
    // Validation errors
    if (message.includes('validation') || message.includes('invalid') || error.status === 400) {
      return ERROR_CATEGORIES.VALIDATION;
    }
    
    // AI processing errors
    if (message.includes('ai') || message.includes('processing') || message.includes('analysis')) {
      return ERROR_CATEGORIES.AI_PROCESSING;
    }
    
    return ERROR_CATEGORIES.UNKNOWN;
  }

  /**
   * Checks if an error is retryable
   * @param {string} category - Error category
   * @param {number} attemptCount - Current attempt count
   * @returns {boolean} Whether the error is retryable
   */
  isRetryable(category, attemptCount = 0) {
    return RETRYABLE_ERRORS.includes(category) && attemptCount < AI_ENVIRONMENT.AI_MAX_RETRIES;
  }

  /**
   * Gets the retry delay for a specific error category
   * @param {string} category - Error category
   * @param {number} attemptCount - Current attempt count
   * @returns {number} Delay in milliseconds
   */
  getRetryDelay(category, attemptCount = 0) {
    const baseDelay = 1000; // 1 second
    
    switch (category) {
      case ERROR_CATEGORIES.RATE_LIMIT:
        return baseDelay * Math.pow(2, attemptCount) * 2; // Exponential backoff with longer delays
      case ERROR_CATEGORIES.TIMEOUT:
        return baseDelay * (attemptCount + 1); // Linear increase
      case ERROR_CATEGORIES.NETWORK:
        return baseDelay * Math.pow(2, attemptCount); // Exponential backoff
      default:
        return baseDelay * (attemptCount + 1);
    }
  }

  /**
   * Gets a user-friendly error message
   * @param {string} category - Error category
   * @param {Error} originalError - The original error (optional)
   * @returns {string} User-friendly error message
   */
  getUserMessage(category, originalError = null) {
    let message = ERROR_MESSAGES[category] || ERROR_MESSAGES[ERROR_CATEGORIES.UNKNOWN];
    
    // Add debug information in development mode
    if (AI_ENVIRONMENT.AI_DEBUG_MODE && originalError) {
      message += ` (Debug: ${originalError.message})`;
    }
    
    return message;
  }

  /**
   * Logs an error with context
   * @param {Error} error - The error to log
   * @param {Object} context - Additional context information
   */
  logError(error, context = {}) {
    const category = this.categorizeError(error);
    const timestamp = new Date().toISOString();
    
    const logEntry = {
      timestamp,
      category,
      message: error.message,
      stack: error.stack,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // Add to error log
    this.errorLog.push(logEntry);
    
    // Keep log size manageable
    if (this.errorLog.length > this.maxLogEntries) {
      this.errorLog.shift();
    }
    
    // Console logging based on category severity
    if (category === ERROR_CATEGORIES.API_KEY || category === ERROR_CATEGORIES.UNKNOWN) {
      console.error('🚨 Critical AI Error:', logEntry);
    } else if (category === ERROR_CATEGORIES.QUOTA_EXCEEDED || category === ERROR_CATEGORIES.RATE_LIMIT) {
      console.warn('⚠️ AI Quota/Rate Limit:', logEntry);
    } else {
      console.log('ℹ️ AI Processing Issue:', logEntry);
    }
    
    // In development, also log to external service (placeholder)
    if (AI_ENVIRONMENT.AI_DEBUG_MODE) {
      this.sendToLoggingService(logEntry);
    }
  }

  /**
   * Processes an error and returns appropriate response
   * @param {Error} error - The error to process
   * @param {Object} context - Additional context
   * @returns {Object} Processed error information
   */
  processError(error, context = {}) {
    const category = this.categorizeError(error);
    this.logError(error, context);
    
    return {
      category,
      userMessage: this.getUserMessage(category, error),
      isRetryable: this.isRetryable(category, context.attemptCount),
      retryDelay: this.getRetryDelay(category, context.attemptCount),
      shouldFallback: !this.isRetryable(category, context.attemptCount),
      timestamp: new Date().toISOString(),
      originalError: AI_ENVIRONMENT.AI_DEBUG_MODE ? error.message : undefined
    };
  }

  /**
   * Creates a fallback response when AI processing fails
   * @param {string} fileName - Original file name
   * @param {Object} context - Additional context
   * @returns {Object} Fallback response
   */
  createFallbackResponse(fileName, context = {}) {
    return {
      categorization: {
        categories: [{ id: 'uncategorized', confidence: 0.5, isPrimary: true }],
        primaryCategory: 'uncategorized',
        reasoning: 'AI processing failed, using fallback categorization'
      },
      title: fileName,
      summary: null,
      extractedData: null,
      processedAt: new Date().toISOString(),
      aiModel: 'fallback',
      fallbackReason: context.error?.category || 'unknown'
    };
  }

  /**
   * Gets error statistics for monitoring
   * @returns {Object} Error statistics
   */
  getErrorStatistics() {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const recentErrors = this.errorLog.filter(
      entry => now - new Date(entry.timestamp).getTime() < oneHour
    );
    
    const categoryCounts = {};
    recentErrors.forEach(entry => {
      categoryCounts[entry.category] = (categoryCounts[entry.category] || 0) + 1;
    });
    
    return {
      totalErrors: this.errorLog.length,
      recentErrors: recentErrors.length,
      categoryCounts,
      lastError: this.errorLog[this.errorLog.length - 1],
      errorRate: this.errorLog.length > 0 ? (recentErrors.length / this.errorLog.length) : 0
    };
  }

  /**
   * Clears the error log
   */
  clearErrorLog() {
    this.errorLog = [];
  }

  /**
   * Sends error to external logging service (placeholder)
   * @param {Object} logEntry - The log entry to send
   */
  sendToLoggingService(logEntry) {
    // Placeholder for external logging service integration
    // Could integrate with services like Sentry, LogRocket, etc.
    if (AI_ENVIRONMENT.AI_DEBUG_MODE) {
      console.log('📊 Would send to logging service:', {
        category: logEntry.category,
        timestamp: logEntry.timestamp,
        context: logEntry.context
      });
    }
  }
}

/**
 * Global error handler instance
 */
export const aiErrorHandler = new AIErrorHandler();

/**
 * Utility function to handle AI service errors
 * @param {Error} error - The error to handle
 * @param {Object} context - Additional context
 * @returns {Object} Processed error information
 */
export const handleAIError = (error, context = {}) => {
  return aiErrorHandler.processError(error, context);
};

/**
 * Utility function to create fallback responses
 * @param {string} fileName - Original file name
 * @param {Object} context - Additional context
 * @returns {Object} Fallback response
 */
export const createFallbackResponse = (fileName, context = {}) => {
  return aiErrorHandler.createFallbackResponse(fileName, context);
};

export default aiErrorHandler;